import React, { useEffect, useState } from "react";
import {
  Box, Flex, Text, SimpleGrid, Select, Input, Button, Spinner, Table, Thead, Tbody, Tr, Th, Td, HStack, VStack, Checkbox
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";

function getRecentDates(n = 30) {
  const dates = [];
  const now = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    // 한국 시간 기준 날짜 추출
    const [yyyy, mm, dd] = d
      .toLocaleDateString("ko-KR", { timeZone: "Asia/Seoul" })
      .split(". ")
      .map(s => s.replace(".", "").padStart(2, "0"));
    dates.push(`${yyyy}-${mm}-${dd}`);
  }
  return dates;
}

export default function AdminShortsMusicRevenue() {
  // 상단 요약
  const [selectedDate, setSelectedDate] = useState(() => getRecentDates(1)[0]);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryWithoutChannel, setSummaryWithoutChannel] = useState(null);
  const [loadingSummaryWithoutChannel, setLoadingSummaryWithoutChannel] = useState(false);
  const [summaryWithoutChannelOnly, setSummaryWithoutChannelOnly] = useState(null);
  const [loadingSummaryWithoutChannelOnly, setLoadingSummaryWithoutChannelOnly] = useState(false);

  // 리스트/필터
  const [filters, setFilters] = useState({
    dateStart: getRecentDates(1)[0],
    dateEnd: getRecentDates(1)[0],
    channel: "",
    shortsCode: "",
    isrc: "",
    emptyChannelOnly: false,
    emptyChannelLicensedOnly: false,
    nonEmptyChannelOnly: false,
    nonEmptyLicensedOnly: false,
  });
  // 나머지 필터 입력 임시 저장용
  const [tempFilters, setTempFilters] = useState({
    channel: "",
    shortsCode: "",
    isrc: "",
  });
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0); // (옵션: 총 개수 표시용)
  const [emptyChannelOnly, setEmptyChannelOnly] = useState(false);
  const [emptyChannelLicensedOnly, setEmptyChannelLicensedOnly] = useState(false);
  const [nonEmptyChannelOnly, setNonEmptyChannelOnly] = useState(false);
  const [nonEmptyLicensedOnly, setNonEmptyLicensedOnly] = useState(false);

  // 요약 fetch
  useEffect(() => {
    async function fetchSummary() {
      setLoadingSummary(true);
      const { data } = await supabase.rpc("get_shorts_profits_summary_by_date_range", {
        p_start_date: filters.dateStart,
        p_end_date: filters.dateEnd,
      });
      setSummary(data && data[0] ? data[0] : null);
      setLoadingSummary(false);
    }
    fetchSummary();
  }, [filters.dateStart, filters.dateEnd]);

  // 채널/비디오ID 없는 요약 fetch
  useEffect(() => {
    async function fetchSummaryWithoutChannel() {
      setLoadingSummaryWithoutChannel(true);
      const { data } = await supabase.rpc("get_short_profits_summary_without_channel_and_videoid", {
        p_start_date: filters.dateStart,
        p_end_date: filters.dateEnd,
      });
      setSummaryWithoutChannel(data && data[0] ? data[0] : null);
      setLoadingSummaryWithoutChannel(false);
    }
    fetchSummaryWithoutChannel();
  }, [filters.dateStart, filters.dateEnd]);

  // 채널만 없는 요약 fetch
  useEffect(() => {
    async function fetchSummaryWithoutChannelOnly() {
      setLoadingSummaryWithoutChannelOnly(true);
      const { data } = await supabase.rpc("get_short_profits_summary_without_channel", {
        p_start_date: filters.dateStart,
        p_end_date: filters.dateEnd,
      });
      setSummaryWithoutChannelOnly(data && data[0] ? data[0] : null);
      setLoadingSummaryWithoutChannelOnly(false);
    }
    fetchSummaryWithoutChannelOnly();
  }, [filters.dateStart, filters.dateEnd]);

  // 리스트 fetch
  useEffect(() => {
    async function fetchList() {
      setLoadingList(true);
      const { data } = await supabase.rpc("get_shorts_profits_summary_admin", {
        p_dateuploadplpl_start: filters.dateStart || null,
        p_dateuploadplpl_end: filters.dateEnd || null,
        p_channel: filters.channel || null,
        p_shortscode: filters.shortsCode || null,
        p_isrc: filters.isrc || null,
        p_empty_channel_only: filters.emptyChannelOnly || false,
        p_empty_channel_licensed_only: filters.emptyChannelLicensedOnly || false,
        p_non_empty_channel_only: filters.nonEmptyChannelOnly || false,
        p_non_empty_licensed_only: filters.nonEmptyLicensedOnly || false,
        page_number: page,
      });
      setList(data || []);
      // total_count가 있으면 전체 행 수로 세팅
      if (data && data.length > 0 && data[0].total_count !== undefined) {
        setTotalRows(Number(data[0].total_count));
      } else {
        setTotalRows(0);
      }
      setLoadingList(false);
    }
    fetchList();
  }, [filters, page]);

  // 날짜 변경 핸들러 (즉시 검색)
  const handleDateChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  // 나머지 필터 입력 핸들러 (입력만 저장)
  const handleTempFilterChange = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  // 검색 버튼/엔터 시 filters에 반영
  const handleSearch = () => {
    setFilters(prev => ({
      ...prev,
      channel: tempFilters.channel,
      shortsCode: tempFilters.shortsCode,
      isrc: tempFilters.isrc,
      emptyChannelOnly: emptyChannelOnly,
      emptyChannelLicensedOnly: emptyChannelLicensedOnly,
      nonEmptyChannelOnly: nonEmptyChannelOnly,
      nonEmptyLicensedOnly: nonEmptyLicensedOnly,
    }));
    setPage(1);
  };

  // 엔터키 핸들러
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // tempFilters와 filters 동기화 (filters 변경 시 tempFilters도 따라감)
  useEffect(() => {
    setTempFilters({
      channel: filters.channel,
      shortsCode: filters.shortsCode,
      isrc: filters.isrc,
    });
  }, [filters.channel, filters.shortsCode, filters.isrc]);

  // 체크박스 변경 핸들러
  const handleCheckboxChange = (type, checked) => {
    if (type === 'emptyChannelOnly') {
      setEmptyChannelOnly(checked);
    } else if (type === 'emptyChannelLicensedOnly') {
      setEmptyChannelLicensedOnly(checked);
    } else if (type === 'nonEmptyChannelOnly') {
      setNonEmptyChannelOnly(checked);
    } else if (type === 'nonEmptyLicensedOnly') {
      setNonEmptyLicensedOnly(checked);
    }
  };

  return (
    <Box minH="100vh" bg="#f7fafd" p={6}>
      {/* 상단 요약 */}
      <Box bg="white" borderRadius="2xl" p={6} boxShadow="md" mb={6}>
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontWeight="bold" fontSize="xl">Shorts 음원 수익 요약</Text>
          <HStack>
            <Text color="gray.400" fontWeight="bold">기간</Text>
            <Input
              type="date"
              value={filters.dateStart}
              onChange={e => handleDateChange("dateStart", e.target.value)}
              w="160px"
              max={getRecentDates(1)[0]}
            />
            <Text color="gray.500">~</Text>
            <Input
              type="date"
              value={filters.dateEnd}
              onChange={e => handleDateChange("dateEnd", e.target.value)}
              w="160px"
              max={getRecentDates(1)[0]}
            />
          </HStack>
        </Flex>
        {loadingSummary || loadingSummaryWithoutChannel || loadingSummaryWithoutChannelOnly ? (
          <Flex justify="center" align="center" minH="80px"><Spinner /></Flex>
        ) : summary ? (
          <SimpleGrid columns={{ base: 2, md: 7 }} spacing={4}>
            <Box>
              <Text color="gray.500" fontSize="sm">예상 수익(전체)</Text>
              <Text fontWeight="bold">₩ {Number(summary.revenue_estimated || 0).toLocaleString()}</Text>
              <Text fontSize="xs" color="gray.400">
                - {Number(summaryWithoutChannel?.revenue_estimated || 0).toLocaleString()}
              </Text>
              <Text fontSize="xs" color="gray.300">
                - {Number(summaryWithoutChannelOnly?.revenue_estimated || 0).toLocaleString()}
              </Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="sm">수수료(레이블리)</Text>
              <Text fontWeight="bold">₩ {Number(summary.revenue_commission || 0).toLocaleString()}</Text>
              <Text fontSize="xs" color="gray.400">
                - {Number(summaryWithoutChannel?.revenue_commission || 0).toLocaleString()}
              </Text>
              <Text fontSize="xs" color="gray.300">
                - {Number(summaryWithoutChannelOnly?.revenue_commission || 0).toLocaleString()}
              </Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="sm">영상 수익(영상권리자)</Text>
              <Text fontWeight="bold">₩ {Number(summary.revenue_video || 0).toLocaleString()}</Text>
              <Text fontSize="xs" color="gray.400">
                - {Number(summaryWithoutChannel?.revenue_video || 0).toLocaleString()}
              </Text>
              <Text fontSize="xs" color="gray.300">
                - {Number(summaryWithoutChannelOnly?.revenue_video || 0).toLocaleString()}
              </Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="sm">적용 수익(크리에이터)</Text>
              <Text fontWeight="bold">₩ {Number(summary.revenue_applied || 0).toLocaleString()}</Text>
              <Text fontSize="xs" color="gray.400">
                - {Number(summaryWithoutChannel?.revenue_applied || 0).toLocaleString()}
              </Text>
              <Text fontSize="xs" color="gray.300">
                - {Number(summaryWithoutChannelOnly?.revenue_applied || 0).toLocaleString()}
              </Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="sm">총 시청시간</Text>
              <Text fontWeight="bold">{Number(summary.total_duration_watch || 0).toLocaleString()}</Text>
              <Text fontSize="xs" color="gray.400">
                - {Number(summaryWithoutChannel?.total_duration_watch || 0).toLocaleString()}
              </Text>
              <Text fontSize="xs" color="gray.300">
                - {Number(summaryWithoutChannelOnly?.total_duration_watch || 0).toLocaleString()}
              </Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="sm">유효 조회수</Text>
              <Text fontWeight="bold">{Number(summary.total_views_count || 0).toLocaleString()}</Text>
              <Text fontSize="xs" color="gray.400">
                - {Number(summaryWithoutChannel?.total_views_count || 0).toLocaleString()}
              </Text>
              <Text fontSize="xs" color="gray.300">
                - {Number(summaryWithoutChannelOnly?.total_views_count || 0).toLocaleString()}
              </Text>
            </Box>
            <Box>
              <Text color="gray.500" fontSize="sm">프리미엄 조회수</Text>
              <Text fontWeight="bold">{Number(summary.total_premium_view_count || 0).toLocaleString()}</Text>
              <Text fontSize="xs" color="gray.400">
                - {Number(summaryWithoutChannel?.total_premium_view_count || 0).toLocaleString()}
              </Text>
              <Text fontSize="xs" color="gray.300">
                - {Number(summaryWithoutChannelOnly?.total_premium_view_count || 0).toLocaleString()}
              </Text>
            </Box>
          </SimpleGrid>
        ) : (
          <Text color="gray.400">데이터 없음</Text>
        )}
      </Box>

      {/* 검색/필터 영역 */}
      <Box bg="white" borderRadius="xl" p={4} boxShadow="sm" mb={6}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <HStack spacing={6} flexWrap="wrap">
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>시작 날짜</Text>
              <Input
                type="date"
                value={filters.dateStart}
                onChange={e => handleDateChange("dateStart", e.target.value)}
                w="160px"
                max={getRecentDates(1)[0]}
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>종료 날짜</Text>
              <Input
                type="date"
                value={filters.dateEnd}
                onChange={e => handleDateChange("dateEnd", e.target.value)}
                w="160px"
                max={getRecentDates(1)[0]}
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>채널명</Text>
              <Input
                value={tempFilters.channel}
                onChange={e => handleTempFilterChange("channel", e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="채널명 입력"
                w="200px"
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>Shorts 코드</Text>
              <Input
                value={tempFilters.shortsCode}
                onChange={e => handleTempFilterChange("shortsCode", e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Shorts 코드 입력"
                w="200px"
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold" mb={1}>ISRC</Text>
              <Input
                value={tempFilters.isrc}
                onChange={e => handleTempFilterChange("isrc", e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="ISRC 입력"
                w="200px"
              />
            </Box>
          </HStack>
          <VStack spacing={2} align="start">
            <Checkbox
              isChecked={emptyChannelOnly}
              onChange={(e) => handleCheckboxChange('emptyChannelOnly', e.target.checked)}
            >
              channel이 비어있는 데이터 보기
            </Checkbox>
            <Checkbox
              isChecked={nonEmptyChannelOnly}
              onChange={(e) => handleCheckboxChange('nonEmptyChannelOnly', e.target.checked)}
            >
              channel이 채워진 데이터만 보기
            </Checkbox>
            <Checkbox
              isChecked={emptyChannelLicensedOnly}
              onChange={(e) => handleCheckboxChange('emptyChannelLicensedOnly', e.target.checked)}
            >
              licensedVideo가 비어있는 데이터 보기
            </Checkbox>
            <Checkbox
              isChecked={nonEmptyLicensedOnly}
              onChange={(e) => handleCheckboxChange('nonEmptyLicensedOnly', e.target.checked)}
            >
              licensedVideo가 채워진 데이터만 보기
            </Checkbox>
          </VStack>
          <Button colorScheme="teal" minW="120px" h="40px" onClick={handleSearch}>검색</Button>
        </Flex>
      </Box>

      {/* 리스트 테이블 */}
      <Box bg="white" borderRadius="2xl" p={4} boxShadow="md">
        {loadingList ? (
          <Flex justify="center" align="center" minH="120px"><Spinner /></Flex>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>채널명</Th>
                <Th>업로드일</Th>
                <Th>Shorts 제목</Th>
                <Th>라이선스 영상</Th>
                <Th>예상 수익</Th>
                <Th>실연권료</Th>
                <Th>수수료</Th>
                <Th>영상 수익</Th>
                <Th>적용 수익</Th>
                <Th>총 시청시간</Th>
                <Th>총 조회수</Th>
                <Th>ISRC</Th>
              </Tr>
            </Thead>
            <Tbody>
              {list.length === 0 ? (
                <Tr><Td colSpan={12} textAlign="center" color="gray.400">데이터 없음</Td></Tr>
              ) : (
                list.map((item, idx) => (
                  <Tr key={idx}>
                    <Td>{item.channel}</Td>
                    <Td>{item.date_upload}</Td>
                    <Td>{item.shorts_title}</Td>
                    <Td>{item.licensed_video}</Td>
                    <Td>{Number(item.revenue_estimated || 0).toLocaleString()}</Td>
                    <Td>{Number(item.revenue_performance || 0).toLocaleString()}</Td>
                    <Td>{Number(item.revenue_commission || 0).toLocaleString()}</Td>
                    <Td>{Number(item.revenue_video || 0).toLocaleString()}</Td>
                    <Td>{Number(item.revenue_applied || 0).toLocaleString()}</Td>
                    <Td>{Number(item.total_duration_watch || 0).toLocaleString()}</Td>
                    <Td>{Number(item.total_views_count || 0).toLocaleString()}</Td>
                    <Td>{item.isrc}</Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        )}
        {/* 페이지네이션 (옵션) */}
        <Flex justify="center" align="center" mt={4} gap={2}>
          <Button size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page === 1}>이전</Button>
          <Text fontWeight="bold" fontSize="sm">{page} / {Math.max(1, Math.ceil(totalRows / 20))}</Text>
          <Button size="sm" onClick={() => setPage(p => p + 1)} isDisabled={page >= Math.ceil(totalRows / 20)}>다음</Button>
        </Flex>
      </Box>
    </Box>
  );
} 